import { useState, FormEvent } from 'react';
import { useSearchParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { authService } from '../../shared/services/auth.service';
import { useApiError } from '../../shared/errors/useApiError';
import { ThemeToggle } from '../../shared/components/ui/ThemeToggle';
import { PasswordField } from '../../shared/components/ui/PasswordField';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { translate } = useApiError();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    if (!token) {
      setError(t('auth.invalidToken'));
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(translate(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: 2,
        py: 4,
        position: 'relative',
      }}
    >
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <ThemeToggle />
      </Box>

      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 460,
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h5" sx={{ mb: 1 }}>
          {t('auth.resetPasswordTitle')}
        </Typography>

        {success ? (
          <Stack spacing={2.5} sx={{ mt: 2 }}>
            <Alert severity="success">{t('auth.resetSuccess')}</Alert>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => navigate('/login')}
            >
              {t('auth.login')}
            </Button>
          </Stack>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('auth.resetPasswordSubtitle')}
            </Typography>

            <Stack spacing={2.5}>
              <PasswordField
                label={t('auth.newPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
              />
              <PasswordField
                label={t('auth.confirmPassword')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
              />
              <Typography variant="caption" color="text.secondary">
                {t('auth.passwordHint')}
              </Typography>

              {error && <Alert severity="error">{error}</Alert>}

              <Button type="submit" variant="contained" size="large" disabled={loading} fullWidth>
                {loading ? t('common.loading') : t('auth.resetPassword')}
              </Button>

              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to="/login" sx={{ color: 'text.secondary' }}>
                  {t('auth.backToLogin')}
                </Link>
              </Typography>
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
